import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  FilterBar,
  FilterField,
  Input,
  PageShell,
  Pagination,
  Skeleton,
} from '@/components/ui';
import { Plus, X } from 'lucide-react';
import type { CouponDefinition } from '@/lib/api-admin';
import { useCouponsQuery } from '../hooks/useCouponsQuery';
import { CouponList } from '../components/CouponList';
import { CouponCreateForm } from '../components/CouponCreateForm';
import { CouponEditForm } from '../components/CouponEditForm';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 250;

export const CouponListPage = () => {
  const { t } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(searchParams.get('create') === '1');
  const [editTarget, setEditTarget] = useState<CouponDefinition | null>(null);

  const closeCreate = useCallback(() => {
    setShowCreate(false);
    if (searchParams.has('create')) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('create');
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const toggleCreate = useCallback(() => {
    if (showCreate) {
      closeCreate();
    } else {
      // 생성/수정 폼은 동시에 열지 않는다(편집 중 등록 토글 시 편집 폼 닫기).
      setEditTarget(null);
      setShowCreate(true);
    }
  }, [showCreate, closeCreate]);

  const handleEdit = useCallback((coupon: CouponDefinition) => {
    setShowCreate(false);
    setEditTarget(coupon);
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setKeyword(keywordInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [keywordInput]);

  const queryParams = keyword ? { keyword, page, size: PAGE_SIZE } : { page, size: PAGE_SIZE };
  const query = useCouponsQuery(queryParams);
  const items = query.data?.items ?? [];
  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;

  const handleReset = useCallback(() => {
    setKeywordInput('');
    setKeyword('');
    setPage(1);
  }, []);

  return (
    <PageShell
      title={t('coupons.title')}
      actions={
        // QA-325: 등록 폼이 열려 있을 때는 [취소] — 등록 버튼과 같은 빨간 + 모양이라 취소인지 알아보기 어려웠다.
        showCreate ? (
          <Button size="sm" variant="outline" onClick={toggleCreate} data-testid="coupon-create-cancel">
            <X size={14} aria-hidden="true" />
            {t('coupons.cancel')}
          </Button>
        ) : (
          <Button size="sm" onClick={toggleCreate} data-testid="coupon-create-open">
            <Plus size={14} aria-hidden="true" />
            {t('coupons.create')}
          </Button>
        )
      }
      data-testid="coupon-list-page"
    >
      {showCreate && (
        <CouponCreateForm
          onSuccess={() => {
            closeCreate();
            void query.refetch();
          }}
        />
      )}

      {editTarget && (
        <CouponEditForm
          coupon={editTarget}
          onCancel={() => setEditTarget(null)}
          onSuccess={() => {
            setEditTarget(null);
            void query.refetch();
          }}
        />
      )}

      <FilterBar
        dense
        data-testid="coupon-filters"
        filters={
          <FilterField label={t('coupons.filter.keyword', '검색어')}>
            <Input
              id="coupon-keyword"
              type="search"
              size="sm"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              placeholder={t('coupons.searchPlaceholder')}
              aria-label={t('coupons.searchPlaceholder')}
            />
          </FilterField>
        }
        actions={
          <Button variant="ghost" size="sm" onClick={handleReset}>
            {t('coupons.reset', '초기화')}
          </Button>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[36px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          <p>{t('coupons.loadFailed')}</p>
          {query.error?.message ? (
            <p className="max-w-md text-xs" style={{ color: 'var(--content-muted)' }}>
              {query.error.message}
            </p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('coupons.retry')}
          </Button>
        </div>
      ) : (
        <CouponList items={items} onEdit={handleEdit} />
      )}

      <Pagination
        total={totalItems ?? 0}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        className="mt-4"
      />
    </PageShell>
  );
};
